import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDate, thaiDateTime } from '../lib/format'

/**
 * Partition console (part 38).
 *
 * This page manages platform.audit_events_p, a new monthly RANGE-partitioned table. It does not
 * touch platform.audit_events: the partition store is additive, and nothing here copies or moves
 * existing audit rows.
 *
 * The probe button is the evidence surface. It inserts one synthetic row and shows the tableoid
 * PostgreSQL reported for that insert, so an operator can see which child actually received the
 * row instead of trusting the month selector.
 *
 * The page is not routed from App.tsx in this part; the API mount is also intentionally unmounted.
 * Both are one-line wiring changes reviewed with the first operator who needs the console.
 */

interface PartitionRow {
  month: string
  table_name: string
  range_start: string
  range_end: string
  estimated_rows: number
  total_bytes: number
  registered: boolean
  last_probe_at?: string
}

interface PartitionList {
  partitions: PartitionRow[]
  parent_table: string
  current_month: string
  latest_month: string
  next_month: string
  total_estimated_rows: number
  total_bytes: number
  server_at: string
  note: string
}

interface RetentionPlan {
  retain_months: number
  current_month: string
  cutoff_month: string
  keep: PartitionRow[]
  drop: PartitionRow[]
  drop_table_names: string[]
  drop_estimated_rows: number
  drop_bytes: number
  server_at: string
  note: string
}

interface PartitionCreateResult {
  created: boolean
  month: string
  partition: PartitionRow
  note: string
}

interface ProbeResult {
  inserted: boolean
  probe_id: number
  tenant_id: string
  month: string
  occurred_at: string
  partition: string
  tableoid: string
  route: {
    at: string
    month: string
    partition: string
    covered: boolean
    reason: string
  }
  note: string
}

const PARTITIONS_KEY = ['partitioning-partitions']
const PLAN_KEY = ['partitioning-plan']

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B'
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${bytes} B`
}

function rangeLabel(row: PartitionRow): string {
  if (!row.range_start || !row.range_end) return '—'
  return `${thaiDate(row.range_start)} → ${thaiDate(row.range_end)}`
}

function monthLabel(month: string): string {
  return month || 'ไม่ทราบเดือน'
}

export function Partitioning() {
  const qc = useQueryClient()
  const [createMonth, setCreateMonth] = useState('')
  const [probeMonth, setProbeMonth] = useState('')
  const [retainMonths, setRetainMonths] = useState(12)
  const [notice, setNotice] = useState<string | null>(null)

  const partitionsQ = useQuery({
    queryKey: PARTITIONS_KEY,
    queryFn: () => api<PartitionList>('/v1/partitioning/partitions'),
    staleTime: 15_000,
  })

  const planQ = useQuery({
    queryKey: [...PLAN_KEY, retainMonths],
    queryFn: () => api<RetentionPlan>(`/v1/partitioning/retention-plan?retain_months=${retainMonths}`),
    staleTime: 10_000,
  })

  const create = useMutation({
    mutationFn: (month: string) =>
      apiWrite<PartitionCreateResult>(
        '/v1/partitioning/partitions/next',
        'POST',
        month.trim() ? { month: month.trim() } : {},
      ),
    onSuccess: (result) => {
      setNotice(
        result.created
          ? `สร้างพาร์ทิชัน ${result.month} แล้ว · ตาราง ${result.partition.table_name}`
          : `พาร์ทิชัน ${result.month} มีอยู่แล้ว · ไม่ได้สร้างซ้ำ`,
      )
      setCreateMonth('')
      void qc.invalidateQueries({ queryKey: PARTITIONS_KEY })
      void qc.invalidateQueries({ queryKey: PLAN_KEY })
    },
  })

  const probe = useMutation({
    mutationFn: (month: string) =>
      apiWrite<ProbeResult>(
        '/v1/partitioning/probe',
        'POST',
        month ? { month } : {},
      ),
    onSuccess: (result) => {
      setNotice(
        `เขียนแถวทดสอบลง ${result.partition} แล้ว · tableoid ${result.tableoid}`,
      )
      void qc.invalidateQueries({ queryKey: PARTITIONS_KEY })
      void qc.invalidateQueries({ queryKey: PLAN_KEY })
    },
  })

  const rows = partitionsQ.data?.partitions ?? []
  const plan = planQ.data
  const dropRows = plan?.drop ?? []
  const latest = partitionsQ.data?.latest_month ?? ''
  const nextMonth = partitionsQ.data?.next_month ?? ''

  const head = (
    <PageHead
      title="พาร์ทิชันตารางเหตุการณ์ตรวจสอบ"
      sub="ตาราง audit_events_p แบ่งพาร์ทิชันแบบ RANGE ตามเดือนเป็นเวลา UTC · สร้างเดือนใหม่ ตรวจแผนเก็บรักษา และทดสอบว่าแถวลงพาร์ทิชันที่ถูกต้อง"
      actions={
        <button
          className="btn"
          disabled={partitionsQ.isFetching || planQ.isFetching}
          onClick={() => {
            void partitionsQ.refetch()
            void planQ.refetch()
          }}
        >
          {partitionsQ.isFetching ? 'กำลังตรวจสอบ…' : 'ตรวจสอบใหม่'}
        </button>
      }
    />
  )

  if (partitionsQ.isLoading) {
    return (
      <>
        {head}
        <div className="stack">
          <Skel h={110} />
          <Skel h={280} />
        </div>
      </>
    )
  }

  if (partitionsQ.isError || !partitionsQ.data) {
    return (
      <>
        {head}
        <Empty
          title="โหลดรายการพาร์ทิชันไม่ได้"
          body="ตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์ และตรวจว่าไมเกรชัน 0105 ถูกนำไปใช้กับฐานข้อมูลนี้แล้ว"
        />
      </>
    )
  }

  return (
    <>
      {head}

      {notice ? (
        <div
          className="card card-pad"
          role="status"
          style={{ marginBottom: 16, borderColor: 'var(--color-hairline-strong)' }}
        >
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="grid-4 stagger" style={{ marginBottom: 22 }}>
        <Stat
          label="พาร์ทิชันที่เชื่อมอยู่"
          value={number(rows.length)}
          unit="เดือน"
          foot={latest ? `ล่าสุด ${latest}` : 'ยังไม่มีพาร์ทิชัน'}
        />
        <Stat
          label="แถวโดยประมาณ"
          value={number(partitionsQ.data.total_estimated_rows)}
          unit="แถว"
          foot="ค่าประมาณจากสถิติของ PostgreSQL"
        />
        <Stat
          label="ขนาดรวม"
          value={formatBytes(partitionsQ.data.total_bytes)}
          foot={`ตารางแม่ ${partitionsQ.data.parent_table}`}
        />
        <Stat
          label="ตัดที่เดือน"
          value={plan?.cutoff_month ?? '—'}
          foot={
            plan
              ? `เก็บ ${plan.retain_months} เดือน · เข้าเกณฑ์ถอด ${number(dropRows.length)} พาร์ทิชัน`
              : 'กำลังคำนวณแผน'
          }
        />
      </div>

      <div className="grid-2" style={{ marginBottom: 22 }}>
        <div className="card card-pad">
          <div className="card-label">สร้างพาร์ทิชันเดือนถัดไป</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            ค่าเริ่มต้นคือเดือนถัดจากพาร์ทิชันล่าสุด ถ้าหน้าต่างพาร์ทิชันตามหลังปฏิทินอยู่
            ระบบจะเลือกเดือนปัจจุบันให้ · ระบุเดือนเองได้เมื่อต้องการเตรียมล่วงหน้าก่อนช่วงข้อมูลสูง
          </p>
          <div className="field">
            <label htmlFor="partition-create-month">เดือนที่ต้องการ (ไม่บังคับ)</label>
            <input
              id="partition-create-month"
              className="input"
              placeholder={nextMonth ? `ค่าเริ่มต้น ${nextMonth}` : 'YYYY-MM'}
              value={createMonth}
              onChange={(event) => setCreateMonth(event.target.value)}
            />
          </div>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <button
              className="btn btn-primary"
              disabled={create.isPending}
              onClick={() => create.mutate(createMonth)}
            >
              {create.isPending ? 'กำลังสร้าง…' : 'สร้างพาร์ทิชัน'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              เดือนถัดไปตามปฏิทินพาร์ทิชัน: {nextMonth || '—'}
            </span>
          </div>
          {create.isError ? (
            <div className="form-error">{errorText(create.error, 'สร้างพาร์ทิชันไม่สำเร็จ')}</div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">ทดสอบเส้นทางของแถว</div>
          <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.8, margin: '10px 0 0' }}>
            เขียนแถวทดสอบหนึ่งแถวผ่านตารางแม่ แล้วอ่าน tableoid ที่ PostgreSQL ใช้จริง ·
            แถวนี้ไม่ใช่เหตุการณ์ตรวจสอบและไม่ถูกเขียนลง platform.audit_events
          </p>
          <div className="field">
            <label htmlFor="partition-probe-month">เดือนที่ต้องการทดสอบ</label>
            <select
              id="partition-probe-month"
              className="select"
              value={probeMonth}
              onChange={(event) => setProbeMonth(event.target.value)}
            >
              <option value="">เดือนปัจจุบัน ({partitionsQ.data.current_month})</option>
              {rows.map((row) => (
                <option key={row.table_name} value={row.month}>
                  {monthLabel(row.month)} · {row.table_name}
                </option>
              ))}
            </select>
          </div>
          <button
            className="btn"
            disabled={probe.isPending}
            onClick={() => probe.mutate(probeMonth)}
          >
            {probe.isPending ? 'กำลังเขียนแถวทดสอบ…' : 'เขียนแถวทดสอบ'}
          </button>
          {probe.isError ? (
            <div className="form-error">{errorText(probe.error, 'เขียนแถวทดสอบไม่สำเร็จ')}</div>
          ) : null}
        </div>
      </div>

      {probe.data ? (
        <div className="card card-pad" style={{ marginBottom: 22 }}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div className="card-label">ผลการทดสอบเส้นทาง</div>
            <span className="badge badge-closed">แถวเข้าแล้ว</span>
          </div>
          <div className="grid-4" style={{ marginTop: 14 }}>
            <div>
              <div className="dim" style={{ fontSize: 11 }}>เดือน</div>
              <div className="mono" style={{ fontSize: 14, marginTop: 4 }}>{probe.data.month}</div>
            </div>
            <div>
              <div className="dim" style={{ fontSize: 11 }}>พาร์ทิชันที่ได้รับแถว</div>
              <div className="mono" style={{ fontSize: 13, marginTop: 4 }}>{probe.data.partition}</div>
            </div>
            <div>
              <div className="dim" style={{ fontSize: 11 }}>tableoid</div>
              <div className="mono" style={{ fontSize: 13, marginTop: 4 }}>{probe.data.tableoid}</div>
            </div>
            <div>
              <div className="dim" style={{ fontSize: 11 }}>เวลาที่ใช้ทดสอบ</div>
              <div style={{ fontSize: 12.5, marginTop: 4 }}>{thaiDateTime(probe.data.occurred_at)}</div>
            </div>
          </div>
          <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 12 }}>
            รหัสแถวทดสอบ {number(probe.data.probe_id)} · route {probe.data.route.reason} ·
            {' '}เดือนตามเวลา UTC {probe.data.route.month} · {probe.data.note}
          </div>
        </div>
      ) : null}

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">พาร์ทิชันที่เชื่อมอยู่</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              ช่วงเวลาเป็น UTC และไม่รวมวันสิ้นสุด · ตัวเลขแถวเป็นค่าประมาณ
            </div>
          </div>
          <span className="chip mono">{partitionsQ.data.parent_table}</span>
        </div>

        <div className="table-wrap" style={{ marginTop: 16 }}>
          {rows.length === 0 ? (
            <div style={{ padding: 16 }}>
              <Empty
                title="ยังไม่มีพาร์ทิชันที่เชื่อมอยู่"
                body="ไมเกรชัน 0105 สร้างเดือน 2026-11 และ 2026-12 ไว้ให้แล้ว · ถ้าไม่พบรายการ ให้ตรวจว่าไมเกรชันถูกนำไปใช้กับฐานข้อมูลนี้"
              />
            </div>
          ) : (
            <table className="table data">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>เดือน</th>
                  <th>ตารางพาร์ทิชัน</th>
                  <th style={{ width: 210 }}>ช่วงเวลา (UTC)</th>
                  <th style={{ width: 95, textAlign: 'right' }}>แถว (ประมาณ)</th>
                  <th style={{ width: 90, textAlign: 'right' }}>ขนาด</th>
                  <th style={{ width: 110 }}>ทะเบียน</th>
                  <th style={{ width: 160 }}>ทดสอบล่าสุด</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.table_name} style={{ cursor: 'default' }}>
                    <td className="mono" style={{ fontSize: 12 }}>{monthLabel(row.month)}</td>
                    <td className="mono" style={{ fontSize: 11.5 }}>{row.table_name}</td>
                    <td className="dim" style={{ fontSize: 11 }}>{rangeLabel(row)}</td>
                    <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>
                      {number(row.estimated_rows)}
                    </td>
                    <td className="tnum dim" style={{ textAlign: 'right', fontSize: 11.5 }}>
                      {formatBytes(row.total_bytes)}
                    </td>
                    <td>
                      {row.registered ? (
                        <span className="badge badge-closed">มีทะเบียน</span>
                      ) : (
                        <span className="chip dim">ไม่มีทะเบียน</span>
                      )}
                    </td>
                    <td className="dim" style={{ fontSize: 11 }}>
                      {row.last_probe_at ? thaiDateTime(row.last_probe_at) : 'ยังไม่ทดสอบ'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="dim" style={{ fontSize: 11, marginTop: 12 }}>
          อ่านข้อมูลเมื่อ {thaiDateTime(partitionsQ.data.server_at)} · {partitionsQ.data.note}
        </div>
      </div>

      <div className="card card-pad" style={{ marginBottom: 22 }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <div className="card-label">แผนการเก็บรักษา</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 4 }}>
              แผนนี้ไม่ลบข้อมูล · การถอดพาร์ทิชันเป็นการกระทำของผู้ดูแลระบบหลังตรวจแผนแล้ว
            </div>
          </div>
          <div className="field" style={{ marginTop: 0, width: 170 }}>
            <label htmlFor="partition-retain-months">เก็บย้อนหลัง (เดือน)</label>
            <input
              id="partition-retain-months"
              className="input"
              type="number"
              min={1}
              max={120}
              value={retainMonths}
              onChange={(event) => {
                const parsed = Number(event.target.value)
                setRetainMonths(Number.isFinite(parsed) ? Math.min(120, Math.max(1, parsed)) : 1)
              }}
            />
          </div>
        </div>

        {planQ.isLoading ? (
          <div style={{ paddingTop: 16 }}><Skel h={140} /></div>
        ) : planQ.isError || !plan ? (
          <div className="form-error" style={{ marginTop: 16 }}>
            {errorText(planQ.error, 'คำนวณแผนการเก็บรักษาไม่สำเร็จ')}
          </div>
        ) : (
          <>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
              <span className="chip">เก็บ {number(plan.retain_months)} เดือน</span>
              <span className="chip">ตัดที่ {plan.cutoff_month}</span>
              <span className="chip gold">เข้าเกณฑ์ถอด {number(dropRows.length)} พาร์ทิชัน</span>
              <span className="chip">{number(plan.drop_estimated_rows)} แถวโดยประมาณ</span>
              <span className="chip">{formatBytes(plan.drop_bytes)}</span>
            </div>

            {dropRows.length === 0 ? (
              <div style={{ marginTop: 16 }}>
                <Empty
                  title="ไม่มีพาร์ทิชันที่เข้าเกณฑ์ถอด"
                  body={`ทุกพาร์ทิชันอยู่ในหน้าต่าง ${plan.retain_months} เดือนที่กำหนด · ลดจำนวนเดือนที่เก็บเพื่อดูรายการที่เข้าเกณฑ์`}
                />
              </div>
            ) : (
              <div className="table-wrap" style={{ marginTop: 16 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>เดือน</th>
                      <th>ตารางที่จะถอด</th>
                      <th style={{ width: 210 }}>ช่วงเวลา (UTC)</th>
                      <th style={{ width: 110, textAlign: 'right' }}>แถว (ประมาณ)</th>
                      <th style={{ width: 90, textAlign: 'right' }}>ขนาด</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dropRows.map((row) => (
                      <tr key={row.table_name} style={{ cursor: 'default' }}>
                        <td className="mono" style={{ fontSize: 12 }}>{monthLabel(row.month)}</td>
                        <td className="mono" style={{ fontSize: 11.5 }}>{row.table_name}</td>
                        <td className="dim" style={{ fontSize: 11 }}>{rangeLabel(row)}</td>
                        <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>
                          {number(row.estimated_rows)}
                        </td>
                        <td className="tnum dim" style={{ textAlign: 'right', fontSize: 11.5 }}>
                          {formatBytes(row.total_bytes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="dim" style={{ fontSize: 11, marginTop: 12 }}>{plan.note}</div>
          </>
        )}
      </div>

      <div className="card card-pad">
        <div className="card-label">ขอบเขตของส่วนนี้</div>
        <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, marginTop: 10 }}>
          ตาราง platform.audit_events_p เป็นตารางใหม่แยกจาก platform.audit_events โดยสิ้นเชิง ·
          ไมเกรชันไม่ได้แก้ไขตารางเดิมและไม่ได้คัดลอกแถวใด ๆ · ตารางลูกทุกตัวเปิด RLS และไม่มีสิทธิ์
          ให้บทบาทแอปเข้าถึงโดยตรง การอ่านและเขียนต้องผ่านตารางแม่เท่านั้น ·
          การเก็บรักษาทำโดยถอดและทิ้งทั้งเดือน ไม่ใช่ลบทีละแถว
        </div>
      </div>
    </>
  )
}
